/**
 * Сети и валюты веб-версии. Зеркало public/api/lib/currencies.php:
 * если меняется список здесь — меняется и там.
 *
 * TON остаётся первой и единственной сетью мини-аппа; всё остальное показывается
 * только вне Telegram (см. isTelegramMiniApp).
 */

export type SupportedNetwork = 'ton' | 'polygon' | 'bsc' | 'ethereum' | 'solana' | 'tron';
export type SupportedCurrency = 'USDT' | 'TON' | 'USDC' | 'POL' | 'SOL' | 'TRX' | 'BNB' | 'ETH';

/** EVM-сети: один адрес кошелька и одна механика оплаты; отличаются chainId и контрактом USDT. */
export const EVM_NETWORKS: SupportedNetwork[] = ['polygon', 'bsc', 'ethereum'];
export const isEvmNetwork = (n: string): boolean => (EVM_NETWORKS as string[]).includes(n);

export interface NetworkMeta {
  id: SupportedNetwork;
  name: string;
  badge: string;
  nativeToken: SupportedCurrency;
  stableToken: SupportedCurrency;
  color: string;
  icon: string;
  tokens: SupportedCurrency[];
  explorerTx: (hash: string) => string;
  explorerAddress: (address: string) => string;
  /** EVM: chainId и параметры для wallet_addEthereumChain. */
  evm?: { chainId: number; chainName: string; rpcUrl: string; explorer: string; nativeName: string };
}

export const NETWORKS: Record<SupportedNetwork, NetworkMeta> = {
  ton: {
    id: 'ton',
    name: 'TON',
    badge: 'The Open Network',
    nativeToken: 'TON',
    stableToken: 'USDT',
    color: '#0098ea',
    icon: '/assets/tonIcon.png',
    tokens: ['TON', 'USDT'],
    explorerTx: (h) => `https://tonviewer.com/transaction/${h}`,
    explorerAddress: (a) => `https://tonviewer.com/${a}`,
  },
  polygon: {
    id: 'polygon',
    name: 'Polygon',
    badge: 'Polygon PoS (EVM)',
    nativeToken: 'POL',
    stableToken: 'USDT',
    color: '#8247e5',
    icon: '/assets/networks/polygon.svg',
    tokens: ['POL', 'USDT', 'USDC'],
    explorerTx: (h) => `https://polygonscan.com/tx/${h}`,
    explorerAddress: (a) => `https://polygonscan.com/address/${a}`,
    evm: { chainId: 137, chainName: 'Polygon Mainnet', rpcUrl: 'https://polygon-rpc.com', explorer: 'https://polygonscan.com', nativeName: 'POL' },
  },
  bsc: {
    id: 'bsc',
    name: 'BNB Chain',
    badge: 'BNB Smart Chain (BEP-20)',
    nativeToken: 'BNB',
    stableToken: 'USDT',
    color: '#f3ba2f',
    icon: '/assets/networks/bsc.svg?v=2',
    tokens: ['BNB', 'USDT', 'USDC'],
    explorerTx: (h) => `https://bscscan.com/tx/${h}`,
    explorerAddress: (a) => `https://bscscan.com/address/${a}`,
    evm: { chainId: 56, chainName: 'BNB Smart Chain', rpcUrl: 'https://bsc-dataseed.binance.org', explorer: 'https://bscscan.com', nativeName: 'BNB' },
  },
  ethereum: {
    id: 'ethereum',
    name: 'Ethereum',
    badge: 'Ethereum (ERC-20)',
    nativeToken: 'ETH',
    stableToken: 'USDT',
    color: '#627eea',
    icon: '/assets/networks/ethereum.svg',
    tokens: ['ETH', 'USDT', 'USDC'],
    explorerTx: (h) => `https://etherscan.io/tx/${h}`,
    explorerAddress: (a) => `https://etherscan.io/address/${a}`,
    evm: { chainId: 1, chainName: 'Ethereum Mainnet', rpcUrl: 'https://ethereum-rpc.publicnode.com', explorer: 'https://etherscan.io', nativeName: 'ETH' },
  },
  solana: {
    id: 'solana',
    name: 'Solana',
    badge: 'Solana Network',
    nativeToken: 'SOL',
    stableToken: 'USDT',
    color: '#9945ff',
    icon: '/assets/networks/solana.svg',
    tokens: ['SOL', 'USDT', 'USDC'],
    explorerTx: (h) => `https://solscan.io/tx/${h}`,
    explorerAddress: (a) => `https://solscan.io/account/${a}`,
  },
  tron: {
    id: 'tron',
    name: 'TRON',
    badge: 'TRON TRC-20',
    nativeToken: 'TRX',
    stableToken: 'USDT',
    color: '#eb0029',
    icon: '/assets/networks/tron.svg',
    tokens: ['TRX', 'USDT'],
    explorerTx: (h) => `https://tronscan.org/#/transaction/${h}`,
    explorerAddress: (a) => `https://tronscan.org/#/address/${a}`,
  },
};

export const NETWORK_ORDER: SupportedNetwork[] = ['ton', 'polygon', 'bsc', 'ethereum', 'solana', 'tron'];

/** Порядок валют в модалке пополнения KredaFi: стейблы, затем нативы шести сетей. */
export const DEPOSIT_ASSET_ORDER: SupportedCurrency[] = [
  'USDT', 'USDC', 'TON', 'POL', 'SOL', 'BNB', 'ETH', 'TRX',
];

export interface CurrencyMeta {
  code: SupportedCurrency;
  /** Как показываем человеку: TON в интерфейсе называется GRAM. */
  label: string;
  icon: string;
  decimals: number;
  displayDecimals: number;
  stable: boolean;
  networks: SupportedNetwork[];
  minDeposit: number;
  minLoan: number;
}

export const CURRENCIES: Record<SupportedCurrency, CurrencyMeta> = {
  USDT: { code: 'USDT', label: 'USDT', icon: '/assets/UsdtLogo.webp', decimals: 6, displayDecimals: 2, stable: true, networks: ['ton', 'polygon', 'bsc', 'ethereum', 'solana', 'tron'], minDeposit: 10, minLoan: 1 },
  TON: { code: 'TON', label: 'GRAM', icon: '/assets/gramIcon.png', decimals: 9, displayDecimals: 4, stable: false, networks: ['ton'], minDeposit: 1, minLoan: 1 },
  USDC: { code: 'USDC', label: 'USDC', icon: '/assets/networks/usdc.svg', decimals: 6, displayDecimals: 2, stable: true, networks: ['solana', 'polygon', 'bsc', 'ethereum'], minDeposit: 10, minLoan: 1 },
  POL: { code: 'POL', label: 'POL', icon: '/assets/networks/polygon.svg', decimals: 18, displayDecimals: 4, stable: false, networks: ['polygon'], minDeposit: 5, minLoan: 1 },
  SOL: { code: 'SOL', label: 'SOL', icon: '/assets/networks/solana.svg', decimals: 9, displayDecimals: 4, stable: false, networks: ['solana'], minDeposit: 0.05, minLoan: 0.01 },
  TRX: { code: 'TRX', label: 'TRX', icon: '/assets/networks/tron.svg', decimals: 6, displayDecimals: 2, stable: false, networks: ['tron'], minDeposit: 20, minLoan: 5 },
  BNB: { code: 'BNB', label: 'BNB', icon: '/assets/networks/bsc.svg?v=2', decimals: 18, displayDecimals: 4, stable: false, networks: ['bsc'], minDeposit: 0.01, minLoan: 0.005 },
  ETH: { code: 'ETH', label: 'ETH', icon: '/assets/networks/ethereum.svg', decimals: 18, displayDecimals: 5, stable: false, networks: ['ethereum'], minDeposit: 0.005, minLoan: 0.001 },
};

/** Контракты токенов (mainnet). */
export const TOKEN_CONTRACTS = {
  polygon: {
    USDT: { address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6 },
    // Нативный USDC (Circle); мостовой USDC.e сервер тоже зачисляет, но платим нативным.
    USDC: { address: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', decimals: 6 },
  },
  bsc: {
    USDT: { address: '0x55d398326f99059fF775485246999027B3197955', decimals: 18 },
    USDC: { address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', decimals: 18 },
  },
  ethereum: {
    USDT: { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', decimals: 6 },
    USDC: { address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', decimals: 6 },
  },
  solana: {
    USDT: { address: 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB', decimals: 6 },
    USDC: { address: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v', decimals: 6 },
  },
  tron: { USDT: { address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', decimals: 6 } },
} as const;

export const POLYGON_CHAIN_ID = 137;

export type EvmNetwork = 'polygon' | 'bsc' | 'ethereum';

export function currencyLabel(code: string): string {
  return CURRENCIES[code as SupportedCurrency]?.label ?? code;
}

export function currencyIcon(code: string): string {
  return CURRENCIES[code as SupportedCurrency]?.icon ?? '/assets/UsdtLogo.webp';
}

export function formatAmount(code: string, value: number | null | undefined): string {
  const meta = CURRENCIES[code as SupportedCurrency];
  const n = Number(value ?? 0);
  const digits = meta?.displayDecimals ?? 2;
  return n.toLocaleString(undefined, { minimumFractionDigits: Math.min(2, digits), maximumFractionDigits: digits });
}

export function isSupportedCurrency(code: string): code is SupportedCurrency {
  return Object.prototype.hasOwnProperty.call(CURRENCIES, code);
}

/**
 * Порядок тикеров рабочего баланса — как на бирже, без сети.
 * USDT из Polygon и USDT из Solana — один USDT; USDC отдельно;
 * ETH/SOL/GRAM и остальные нативки — своим тикером.
 */
export const WORKING_BALANCE_ORDER: SupportedCurrency[] = [
  'USDT', 'USDC', 'TON', 'ETH', 'BNB', 'SOL', 'POL', 'TRX',
];

/** Порядок валют в форме «Получить займ» (KredaFi, не мини-апп). */
export const BORROW_FORM_CURRENCY_ORDER: SupportedCurrency[] = [
  'USDT', 'USDC', 'TON', 'POL', 'SOL', 'BNB', 'ETH', 'TRX',
];

const BORROW_MAX_USD = 1000;
const BORROW_MIN_STABLE = 50;

/** Лимиты суммы заявки на займ под сертификат (зеркало create_loan.php). */
export function borrowAmountLimits(
  currency: SupportedCurrency,
  tonPrice: number | null,
  assetPrices: Record<string, number | null | undefined> = {}
): { min: number; max: number; hint: string } {
  const meta = CURRENCIES[currency];
  if (meta.stable) {
    return {
      min: BORROW_MIN_STABLE,
      max: BORROW_MAX_USD,
      hint: `Min. ${BORROW_MIN_STABLE} ${meta.label} — Max. ${BORROW_MAX_USD} ${meta.label}`,
    };
  }
  const price =
    currency === 'TON'
      ? tonPrice
      : assetPrices[currency] ?? null;
  const min = currency === 'TON' ? Math.max(1, meta.minLoan) : meta.minLoan;
  if (price && price > 0) {
    const max = Math.max(min, Math.floor(BORROW_MAX_USD / price));
    return {
      min,
      max,
      hint: `Min. ${min} ${meta.label} — Max. ${max} ${meta.label}`,
    };
  }
  const fallbackMax = currency === 'TON' ? 200 : Math.max(min, 100);
  return {
    min,
    max: fallbackMax,
    hint: `Min. ${min} ${meta.label} — Max. ${fallbackMax} ${meta.label}`,
  };
}

/**
 * Какие тикеры показать на рабочем балансе и в выдаче займа.
 * Мини-апп: только GRAM + USDT. Веб/PWA: USDT всегда, остальные — только при ненулевом балансе.
 */
export function workingBalanceTickers(
  isMiniApp: boolean,
  balances: Record<string, number>
): SupportedCurrency[] {
  if (isMiniApp) return ['TON', 'USDT'];
  return [...WORKING_BALANCE_ORDER];
}

export function isSupportedNetwork(id: string): id is SupportedNetwork {
  return Object.prototype.hasOwnProperty.call(NETWORKS, id);
}

/** Валюты, доступные в сети (первая — стейбл, как основной сценарий). */
export function tokensOfNetwork(network: SupportedNetwork): SupportedCurrency[] {
  return NETWORKS[network].tokens;
}

export function truncateAddress(addr?: string | null, head = 6, tail = 4): string {
  if (!addr) return '';
  if (addr.length <= head + tail + 1) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}
